<template>
  <form v-on:submit.prevent="submitReview" novalidate>
    <fieldset :disabled="disabled">
      <div class="form-group text-center text-sm-left" >
        <label>{{ $t('Overall Rating') }} *</label>
        <StarRating size="big" :rating="review.rating" v-on:change="updateRating"></StarRating>
        <div class="invalid-feedback" :class="{'d-block' : errors.rating}"  v-if="errors.rating" v-cloak>
          {{ errors.rating[0] }}
        </div>
      </div>
      <div class="form-group" v-if="templateOptions.includes('hasTitle')">
        <label>{{ $t('Review title') }} *</label>
        <input type="text" v-model="review.title" :class="{ 'is-invalid': errors.title}"
               maxlength="50"
               class="form-control form-control-lg" :placeholder="$t('Summarize your impression on the product')" required />
        <div class="invalid-feedback" :class="{'d-block' : errors.title}"  v-if="errors.title" v-cloak>
          {{ errors.title[0] }}
        </div>
      </div>

      <div class="form-group" v-if="templateOptions.includes('hasDescription')">
        <label>{{ $t('Your detailed product review') }} * <small>({{ $t('50 characters minimum') }})</small></label>
        <textarea v-model="review.description"
                  :class="{ 'is-invalid': errors.description, 'is-valid' : isValidDescription}"
                  cols="30"
                  rows="5"
                  class="form-control"
                  :placeholder="$t('What was your impression of the product while testing? What did you like or dislike about the product? What conditions where present while testing the product?')"
                  required></textarea>
        <div class="form-text text-muted">
          {{totalDescriptionCaracters}}/{{ $t('50 characters (minimum)') }}
          <span v-if="totalDescriptionCaracters > MIN_DESCRIPTION_CHARACTERS*8">🤩</span>
          <span v-else-if="totalDescriptionCaracters > MIN_DESCRIPTION_CHARACTERS*4">🤗</span>
          <span v-else-if="totalDescriptionCaracters > MIN_DESCRIPTION_CHARACTERS*2">😃</span>
          <span v-else-if="totalDescriptionCaracters > MIN_DESCRIPTION_CHARACTERS">🙂</span>
          <span v-else>🙁</span>
        </div>
        <div class="invalid-feedback" :class="{'d-block' : errors.description}"  v-if="errors.description" v-cloak>
          {{ errors.description[0] }}
        </div>
      </div>

      <div v-for="extraCriteria in extraCriterias" :key="extraCriteria.id">
        <component
          :is="criteriaComponentName(extraCriteria.type)"
          :config="extraCriteria"
          :errors="getReviewCriteriasError(extraCriteria.id)"
          @change="updateExtraCriteria($event, extraCriteria.id)"
        />
      </div>

      <div class="form-group" v-if="templateOptions.includes('hasComment')">
        <label>{{ $t('Comments') }} </label>
        <textarea v-model="review.comment"
                  :class="{ 'is-invalid': errors.comment}"
                  cols="30"
                  rows="2"
                  class="form-control"
                  :placeholder="$t('Comments on the reception, the organization, the advice on the product…')"></textarea>
        <div class="invalid-feedback" :class="{'d-block' : errors.comment}"  v-if="errors.comment" v-cloak>
          {{ errors.comment[0] }}
        </div>
      </div>

      <template v-for="field in [
        {
          name: 'recommended',
          label: 'Would you recommend this product to a friend?',
        },
        {
          name: 'purchase',
          label: 'Would you be ready to buy this product?',
        }
      ].filter((field) => templateOptions.includes('has' + field.name.charAt(0).toUpperCase() + field.name.slice(1)))" :key="field.name">
        <div class="form-group">
          <label class="d-block">
            {{ $t(field.label) }} *
          </label>
          <div class="form-check form-check-inline">
            <label class="form-check-label">
              <input class="form-check-input" type="radio" value='1'
                     v-model="review[field.name]" :class="{ 'is-invalid': errors[field.name]}">
              {{ $t('Yes') }}
            </label>
          </div>
          <div class="form-check form-check-inline">
            <label class="form-check-label">
              <input class="form-check-input" type="radio" value='0'
                     v-model="review[field.name]" :class="{ 'is-invalid': errors[field.name]}">
              {{ $t('No') }}
            </label>
          </div>
          <div class="invalid-feedback" :class="{'d-block' : errors[field.name] }"
               v-if="errors[field.name]" v-cloak>
            {{ errors[field.name][0] }}
          </div>
        </div>
      </template>
      <hr>
      <div class="form-group">
        <label>{{ $t('First name or pseudo') }} *</label>
        <input type="text"
               v-model="review.author"
               :class="{ 'is-invalid': errors.author}"
               class="form-control"
               required />
        <small class="form-text text-muted">
          {{ $t('Your name / nickname is published with your review') }}
        </small>
        <div class="invalid-feedback" :class="{'d-block' : errors.author}"  v-if="errors.author" v-cloak>
          {{ errors.author[0] }}
        </div>
      </div>
      <div class="form-group">
        <div class="form-check">
          <label class="form-check-label">
            <input type="checkbox" class="form-check-input" v-model="review.cgu" :class="{ 'is-invalid': errors.cgu}" required>
            {{ $t('I have read and I accept the terms and conditions') }}
            <a :href="cguUrl" target="_blank">{{ $t('of publication of the reviews') }}</a>
            {{ $t('and') }}
            <a :href="privacyUrl" target="_blank">{{ $t('the privacy policy') }}</a>. *
          </label>
        </div>
        <div class="invalid-feedback" :class="{'d-block' : errors.cgu}"  v-if="errors.cgu" v-cloak>
          {{ errors.cgu[0] }}
        </div>
      </div>
      <div class="form-group align-center">
        <button type="submit" class="btn btn-primary" name="button">
           <span class="d-flex">
              <Spinner class="mr-3" style="width: 20px" v-if="disabled"></Spinner>
              {{ $t('Post Review') }}
           </span>
        </button>
      </div>
    </fieldset>
  </form>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import CriteriaRating from '@/components/frontend/review/CriteriaRating.vue';
import CriteriaSelect from '@/components/frontend/review/CriteriaSelect.vue';
import CriteriaCheckbox from '@/components/frontend/review/CriteriaCheckbox.vue';
import CriteriaText from '@/components/frontend/review/CriteriaText.vue';
import CriteriaTextarea from '@/components/frontend/review/CriteriaTextarea.vue';
import CriteriaUpload from "@/components/frontend/review/CriteriaUpload.vue";
import StarRating from "@/components/frontend/review/StarRating.vue";
import Spinner from "@/components/frontend/ui/Spinner.vue";
import jsonToFormData from "@/components/frontend/review/jsonToFormData";
import { useNotification } from "@kyvg/vue3-notification";

const MIN_DESCRIPTION_CHARACTERS = 50;

const { notify } = useNotification();

// Props
const props = defineProps({
  author: String,
  lang: String,
  extraCriterias: Array,
  reviewable: {
    required: true,
    type: Object,
  },
  productTest: {
    type: Object,
    default: null,
  },
  baseUrl: {
    required: true,
    type: String,
  },
  cguUrl: {
    required: true,
    type: String,
  },
  privacyUrl: {
    required: true,
    type: String,
  },
  rating: {
    default: null,
  },
  templateOptions: {
    type: Array,
    default : function(){
      return [
        'hasTitle',
        'hasComment',
        'hasDescription',
        'hasPurchase',
        'hasRecommended',
      ]
    },
  }
});

// State
const review = reactive({
  rating: null,
  title: '',
  description: '',
  reviewcriterias: {},
  comment: '',
  recommended: null,
  purchase: null,
  author: '',
  cgu: false,
});
const disabled = ref(false);
const errors = ref({});

// Computed
const isValidDescription = computed(() => review.description.length - MIN_DESCRIPTION_CHARACTERS > 0);
const totalDescriptionCaracters = computed(() => review.description.length);

// Methods
function updateRating(e) {
  review.rating = e;
}
function updateExtraCriteria(e, id) {
  review.reviewcriterias[id] = e;
}
function getReviewCriteriasError(id) {
  let e = Object.keys(errors.value).reduce((accumulator, key) => {
    if (key === 'reviewcriterias.' + id) {
      accumulator = errors.value[key];
      return accumulator;
    }
    if (key.startsWith('reviewcriterias.' + id)) {
      let k = key.replace('reviewcriterias.' + id + '.', '').split('.');
      let message = [];
      accumulator[k[0]] = errors.value[key];
    }
    return accumulator;
  }, []);
  return e;
}

const criteriaComponentName = (type) => {
  switch (type) {
    case 'rating':
      return CriteriaRating;
    case 'select':
      return CriteriaSelect;
    case 'checkbox':
      return CriteriaCheckbox;
    case 'text':
      return CriteriaText;
    case 'textarea':
      return CriteriaTextarea;
    case 'upload':
      return CriteriaUpload;
    default:
      throw new Error(`Unknown criteria type: ${type}`);
  }
};

// Emits
const emit = defineEmits(['successfully-submitted']);
const submitReview = async () =>  {
  disabled.value = true;
  const reviewData = {
    ...review,
    'lang' : props.lang,
    'reviewable': props.reviewable,
    'product_test' : props.productTest,
  }
  let formData = jsonToFormData(reviewData);

  const baseUrl = props.baseUrl.endsWith('/') ? props.baseUrl.slice(0, -1) : props.baseUrl;

  /*
  at this time the CSRF token is not supported by the API, so we don't need to fetch it.
  If you want to use it, uncomment the following lines and update the API to support it
  await fetch(baseUrl + "/sanctum/csrf-cookie", {
    credentials: "include"
  });
  const csrfToken = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='))?.split('=')[1];
  */
  try {
    const response = await fetch(baseUrl + '/api/reviews', {
      method: 'POST',
      headers: {
        "accept": "application/json",
    //    "X-XSRF-TOKEN": decodeURIComponent(csrfToken) // 👈 csrf
      },
      credentials: "include",
      body: formData, // JSON.stringify(reviewData),

    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        throw new Error('Erreur réseau');
      }
      throw errorData;
    }

    const data = await response.json();
    emit('successfully-submitted', {
      'review': data.review,
      'productTestToReview': data.toReview,
    });
  } catch (error) {
    if (error && error.message && typeof error.message === 'string') {
      notify({
        group: 'errors',
        type: 'error',
        title: error.message
      });
    } else if (typeof error === 'object') {
      errors.value = {
        ...error.message
      };
      Object.values(error.message || {}).forEach(function(message) {
        notify({
          group: 'errors',
          type: 'error',
          title: message[0],
        });
      });
    } else {
      errors.error = 'error';
    }
    disabled.value = false;
  }
}

// Lifecycle
onMounted(() => {
  review.author = props.author;
  if (props.rating) {
    review.rating = parseInt(props.rating);
  }
});
</script>

<style>

</style>
